import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { revisionsLeadSteps, revisionsMemberSteps } from "guides/features/Evaluation";
import { LeadRevisionsView } from "./components/LeadRevisionsView";
import { PanelRevisionsView, usePanelRevisionsHasData } from "./components/PanelRevisionsView";

const LEAD_REVISION_INSTRUCTIONS = [
	"Request document revisions from suppliers if their submissions need updating.",
	"Track the status of each revision request and review uploaded revised documents.",
	"Once all revisions are received, proceed to the next step.",
];

const MEMBER_REVISION_INSTRUCTIONS = [
	"Review any revision requests raised by the RFx Lead.",
	"Updated supplier documents will appear here once revisions are uploaded.",
];

interface RevisionsStepProps {
	evaluationId?: number;
	isLoading?: boolean;
	isRfxLead?: boolean;
	rfxId: number;
	rfxSubmissionId?: number;
}

export const RevisionsStep: React.FC<RevisionsStepProps> = ({
	rfxId,
	evaluationId,
	rfxSubmissionId,
	isRfxLead = false,
	isLoading = false,
}) => {
	// Must run before the early returns (rules of hooks). Both underlying queries are
	// `enabled: !!rfxId && !!evaluationId`, so the 0 fallback fetches nothing.
	const hasPanelRevisions = usePanelRevisionsHasData(rfxId, evaluationId ?? 0);

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	if (!evaluationId) {
		return (
			<div data-testid="revisions-step">
				<div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm">
					Evaluation must be started before revisions are available.
				</div>
			</div>
		);
	}

	if (isRfxLead) {
		return (
			<div data-testid="revisions-step" className="space-y-4">
				<InstructionsInfoBox instructions={LEAD_REVISION_INSTRUCTIONS} guideSteps={revisionsLeadSteps} />
				<LeadRevisionsView
					rfxId={rfxId}
					evaluationId={evaluationId}
				/>
			</div>
		);
	}

	return (
		<div data-testid="revisions-step" className="space-y-4">
			{/* No revisions yet means `panel-revisions-view` (the tour's only anchor)
			    is not rendered, so withhold the steps — InstructionsInfoBox hides
			    "Show Me" when there is nothing to show. */}
			<InstructionsInfoBox
				instructions={MEMBER_REVISION_INSTRUCTIONS}
				guideSteps={hasPanelRevisions ? revisionsMemberSteps : undefined}
			/>
			<PanelRevisionsView
				rfxId={rfxId}
				evaluationId={evaluationId}
			/>
		</div>
	);
};
